<template>
    <DialogLayout :class-name="'ai-parse-resume-dialog'" @close="handleClose">
        <template #title>{{ t('employee.addCandidateDialog.aiParseResume') }}</template>
        <template #default>
            <div class="dialog-content">
                <div class="upload-section">
                    <FileUpload
                        :title="t('employee.addCandidateDialog.uploadResume')"
                        :max-count="1"
                        :max-size="maxFileSizeMb"
                        :tips="t('employee.form.documents.other.tips')"
                        accept=".webp,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls,.pdf"
                        :file-list="uploadedFile"
                        :disabled="false"
                        :show-preview="false"
                        @update:file-list="handleFileListUpdate"
                        @upload="handleFileUpload"
                        @delete="handleFileDelete"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button
                type="primary"
                :loading="submitting"
                :disabled="isConfirmDisabled"
                :class="{ 'is-disabled-opacity': isConfirmDisabled }"
                @click="handleConfirm"
            >
                {{ t('employee.addCandidateDialog.startParse') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, computed, h } from 'vue'
import { ElMessage } from 'element-plus'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue'
import { uploadAttachmentApi, resumeAnalysisApi, type AttachmentInfo } from '@/api/candidate'
import { useI18n } from 'vue-i18n'
import FileUpload from '@/components/business-components/file-upload/index.vue'
import type { AiParseResumeDialogProps, AiParseResumeDialogResult } from './ai-parse-resume-dialog-types'
import loadingGif from '@/assets/icons/loading.gif';

interface UploadedFile {
  uid?: string
  name?: string
  fileName?: string
  url?: string
  fileKey?: string
}

const { t } = useI18n()
const props = withDefaults(defineProps<AiParseResumeDialogProps>(), {
    maxFileSizeMb: 10
})

const emit = defineEmits<{
  cancel: []
  confirm: [data: AiParseResumeDialogResult]
  close: []
}>()

const uploading = ref(false)
const submitting = ref(false)
const uploadedFile = ref<UploadedFile[]>([])
const uploadedAttachment = ref<AttachmentInfo | null>(null)
const loadingModal = ref<ReturnType<typeof ElMessage> | null>(null)

const maxFileSizeMb = computed(() => props.maxFileSizeMb)

const isConfirmDisabled = computed(() => {
  return uploadedFile.value.length === 0 || uploading.value || submitting.value
})

const handleFileListUpdate = (fileList: UploadedFile[]) => {
  uploadedFile.value = fileList
}

// 后端返回的附件字段存在大小写差异（s3Key/s3key、fileName/name），做兼容取值
const getAttachmentKey = (attachment: AttachmentInfo | null | undefined) => {
  const raw = attachment as unknown as { s3Key?: string; s3key?: string } | null | undefined
  return raw?.s3Key ?? raw?.s3key ?? ''
}

const getAttachmentName = (attachment: AttachmentInfo) => {
  const raw = attachment as unknown as { fileName?: string; name?: string }
  return raw.fileName ?? raw.name ?? ''
}

const handleFileUpload = async (file: File) => {
  try {
    uploading.value = true
    const result = await uploadAttachmentApi(file)
    uploadedAttachment.value = result
    uploadedFile.value = [{
      fileName: getAttachmentName(result),
      url: result.url,
      fileKey: getAttachmentKey(result)
    }]
    ElMessage.success(t('common.uploadSuccess'))
  } catch (error) {
    console.error('上传简历失败:', error)
    ElMessage.error((error as Error).message || t('common.uploadFailed'))
  } finally {
    uploading.value = false
  }
}

const handleFileDelete = () => {
  uploadedFile.value = []
  uploadedAttachment.value = null
}

const handleCancel = () => {
  submitting.value = false
  if (loadingModal.value) {
    loadingModal.value.close()
    loadingModal.value = null
  }
  emit('cancel')
}

const handleConfirm = async () => {
  if (uploadedFile.value.length === 0) {
    ElMessage.error(t('employee.addCandidateDialog.uploadRequired'))
    return
  }

  submitting.value = true
  loadingModal.value = ElMessage({
    message: t('common.parseLoading'),
    duration: 0,
    showClose: false,
    icon: h('img', { src: loadingGif, style: 'width: 20px; height: 20px;' })
  });
  try {
    const s3Key = getAttachmentKey(uploadedAttachment.value)
    if (!s3Key) {
      ElMessage.error(t('employee.addCandidateDialog.uploadRequired'))
      return
    }
    const data = await resumeAnalysisApi(s3Key)
    const parsed = (data && typeof data === 'object' && 'data' in data)
      ? (data as { data?: Record<string, unknown> }).data
      : data
    ElMessage.success(t('common.parseSuccess'))
    emit('confirm', {
      attachment: uploadedAttachment.value || undefined,
      data: (parsed || undefined) as Record<string, unknown> | undefined
    })
  } catch (error) {
    console.error('AI 解析简历失败:', error)
    ElMessage.error(t('common.parseFailed'))
  } finally {
    if (loadingModal.value) {
      loadingModal.value.close()
      loadingModal.value = null
    }
    submitting.value = false
  }
}

const handleClose = () => {
  if (!submitting.value) {
    emit('close')
  }
}
</script>

<style scoped lang="scss">
.ai-parse-resume-dialog {
  min-width: 520px;
  background: #ffffff;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.upload-section {
  background: #F7F8FA;
  border-radius: 8px;
  padding: 12px 8px;
  gap: 8px;
  display: flex;
  flex-direction: column;
}

.is-disabled-opacity {
    background-color: #8755DE !important;
    opacity: 0.5;
}
</style>
